// configDefaults — the per-section default config objects, moved out of their
// component files so the load/save modules can import them WITHOUT statically
// pulling those (now lazy-loaded) components into the main chunk.

export const DISCOUNT_TYPES = [
    { code: '999902', labelKey: 'cfg_proc_disc_type_gift_voucher',             label: 'Gift voucher',             defaultEnabled: true,  alwaysOn: false },
    { code: '999903', labelKey: 'cfg_proc_disc_type_deposit',                  label: 'Deposit',                  defaultEnabled: false, alwaysOn: false },
    { code: '999904', labelKey: 'cfg_proc_disc_type_voucher',                  label: 'Voucher',                  defaultEnabled: true,  alwaysOn: false },
    { code: '999905', labelKey: 'cfg_proc_disc_type_customer_discount',        label: 'Customer discount',        defaultEnabled: true,  alwaysOn: false },
    { code: '999907', labelKey: 'cfg_proc_disc_type_commercial_coupon',        label: 'Commercial coupon',        defaultEnabled: true,  alwaysOn: false },
    { code: '999908', labelKey: 'cfg_proc_disc_type_discount_type',            label: 'Discount type',            defaultEnabled: false, alwaysOn: false },
    { code: '999909', labelKey: 'cfg_proc_disc_type_customer_discount_coupon', label: 'Customer discount coupon', defaultEnabled: true,  alwaysOn: false },
];

// Fallbacks for values a shop has not stored — the same ones the storefront's
// carousel falls back to (home-carousel.component loadConfig), so the modal
// never shows something else than what the shop does.
export const CAROUSEL_LANG_SETTINGS_DEFAULT = {
    display: {
        fullWidth:   false,
        maxHeight:   0,          // 0 = no limit
        maxHeightUnit: 'vh',     // px | vh
        spacingTop:  0,
        spacingBottom: 0,
        imageFit:    'contain',  // contain | cover | fill
    },
    hover: {
        effect:        'none',   // none | zoom | second_image
        zoomLevel:     140,
        animationSpeed:0.3,      // seconds here, milliseconds in the setting
    },
    behavior: {
        showArrows:        true,
        arrowsOnHover:     false,
        arrowsBackground:  false,
        autoAdvance:       true,
        timePerSlide:      10,
        transition:        'fade',
    },
    textOverlay: {
        desktop: { background: '#ffffff', textColor: '#000000', position: 'bottom-center', size: 12, width: 10, bold: false, italic: false, underline: false, strike: false },
        mobile:  { background: '#ffffff', textColor: '#000000', position: 'bottom-center', size: 12, width: 10, bold: false, italic: false, underline: false, strike: false },
        onlyOnHover:        true,
        alwaysVisible:      false,
        useDesktopOnMobile: false,
    },
};

export const IMAGE_GRID_LANG_SETTINGS_DEFAULT = {
    layout: {
        columns:           3,
        gap:               8,
        // [top, bottom] margin around the grid block, in px. Backed by
        // IMAGE_GRID_CONFIG_{lang}.margin (mig164).
        margin:            [20, 0],
        // "Use the same grid configuration for smaller screens." Backed
        // by IMAGE_GRID_KEEP_GRID_ON_MOBILE_{N|E|F} (mig112 + mig158).
        keepGridOnMobile:  true,
    },
    hover: {
        effect:         'none',  // none | zoom | second_image
        zoomLevel:      140,
        animationSpeed: 0.3,     // seconds here, milliseconds in the setting
    },
    textOverlay: {
        desktop: { background: '#ffffff', textColor: '#000000', position: 'bottom-center', size: 12, width: 100, bold: false, italic: false, underline: false, strike: false },
        mobile:  { background: '#ffffff', textColor: '#000000', position: 'bottom-center', size: 12, width: 10,  bold: false, italic: false, underline: false, strike: false },
        onlyOnHover:        true,
        alwaysVisible:      false,
        useDesktopOnMobile: true,
    },
};

export const DEFAULT_CAROUSEL_CONFIG = {
    slides: { nl: [], en: [], fr: [] },
    settings: {
        nl: CAROUSEL_LANG_SETTINGS_DEFAULT,
        en: CAROUSEL_LANG_SETTINGS_DEFAULT,
        fr: CAROUSEL_LANG_SETTINGS_DEFAULT,
    },
};

export const DEFAULT_IMAGE_GRID_CONFIG = {
    cells: { nl: [], en: [], fr: [] },
    settings: {
        nl: IMAGE_GRID_LANG_SETTINGS_DEFAULT,
        en: IMAGE_GRID_LANG_SETTINGS_DEFAULT,
        fr: IMAGE_GRID_LANG_SETTINGS_DEFAULT,
    },
};

export const PROCESSING_CONFIG_DEFAULT = {
    // GENERAL
    access: {
        b2bOnly:         false,
        posCode:         '0199',
        guestCustomerId: '34',
        allowGuestPickup:false,
    },
    warehouse: '01',
    // Order-processing module (PROCESS_API_MODULE, legacy SuperUser tab).
    // Decides whether an order becomes a RESERVATION or a RECEIPT on the POS --
    // and with it whether `warehouse` above is used at all: the RECEIPT branch
    // derives the store from the first two digits of the receipt POS id
    // instead (see OrderController::handleBasicData). SoftTouch-only field.
    apiModule: 'RESERVATION',
    // Kasticket (receipt) POS IDs — which POS the receipt is processed on vs.
    // printed on. Legacy SuperUser tab; format "0101" (store 2 + pos 2).
    receiptPos: {
        process: '0101',  // PROCESS_RECEIPT_POS_ID_PROCESS
        print:   '0101',  // PROCESS_RECEIPT_POS_ID_RECEIPT
    },
    discountTypes: DISCOUNT_TYPES.reduce((acc, d) => {
        acc[d.code] = { enabled: d.defaultEnabled, acceptAsPayment: d.defaultEnabled, showInEmail: d.defaultEnabled };
        return acc;
    }, {}),
    discountBarcode: '',  // ITEM_DISCOUNT — barcode of the back-office discount product
    cart: {
        allowDiscountCodes:       true,
        allowNegativeTotals:      false,
        limitQuantityPerItem:     true,
        maxQuantityPerItem:       10,
        maxUniqueItemsInCart:     40,
        clearOnBankTransfer:      false, // CART_CLEAR_ON_BANK_TRANSFER (mig210)
    },
    registration: {
        phoneRequired:            true,
        defaultSalutation:        '9002',
        defaultCountry:           '',
        showPurchaseHistory:      true,
        showCustomerDiscount:     true,
    },
    externalStock: {
        iconName:  'truck-ramp-box',
        iconColor: '#f57c00',
    },
    ticketPrinting: {
        regular: { pickup: true,  shipped: true  },
        compact: { pickup: false, shipped: false },
        message: { pickup: true,  shipped: true  },
    },
    // PAYMENTS
    payments: {
        location:           'onlineAndStore',
        allowInvoice:       true,
        provider:           'msp',  // PAYMENT_PROVIDER — only MultiSafePay (Europabank/mpi is deprecated)
        paymentType:        '1',    // PROCESS_PAYMENT_TYPE (1-9)
    },
    // SHIPPING
    shipping: {
        method:                  'pickupAndShipping',
        routeExternalThroughShop:false,
        externalStockEnabled:    false,            // gated by Data sync
        defaultCost:             6,
        defaultPickupCost:       6,   // PICKUP_POINT_COST (parcel-point pickup)
        freeFromAmount:          99,
        shippingCostItem:        '17581401010101',
    },
    // CUSTOMER FIELDS are NOT settings — they're a CRUD list backed by
    // `customers_categories_groups`, owned by the parent (see customerCategoryGroups).
};

export const SYNC_CONFIG_DEFAULT = {
    // GENERAL
    sourceWebshop: '1',
    storesIncluded: [],            // expanded from FETCH_FOR_STORES ('00' = all) at load
    stockFiltering: {
        onlySyncWithStock:        false,
        useExternalStockProvider: false,
        showExternalOnlyProducts: false,
    },
    // FIELD MAPPING — 7 fixed levels, each reading from a source category code.
    categoryHierarchy: [1, 2, 3, 4, 5, 6, 7].map(n => ({ level: n, source: String(n).padStart(2, '0') })),
    productFields: { name: 'detail2', reference: 'detail1' },
    languageMapping: { nl: '1', en: '2', fr: '3' },
    // IMAGES
    imageSourceTypes: { primary: '01', alternative: '02' },
    imageProcessing: {
        useV2: false,
        sizes: {
            thumb:     { width: 100, height: 160  },
            preview:   { width: 300, height: 500  },
            processed: { width: 800, height: 1300 },
        },
        backgroundFillColor: '#ffffff',
    },
};

export const CADEAUBON_CONFIG_DEFAULT = {
    availability: {
        showInNavigation: false,
        allowPhysical: true,
        allowDigital: true,
    },
    amounts: {
        presets: [10, 25, 50, 100],
        allowCustom: true,
        customMin: 10,
        customMax: 250,
    },
    discounts: {
        countTowardTotals: false,
        applyToVoucherOnly: false,
        applyToMixedOrders: false,
    },
    shipping: {
        cost: 0,
        freeFrom: 999,
    },
    personalization: {
        // Mirrors the old admin: two voucher-type selects + one toggle.
        infoOnType: 'BOTH',           // CHEQUES_ALLOW_INFO_ON_TYPE    — NONE|PHYSICAL|DIGITAL|BOTH
        sendDigitalDirectly: true,    // CHEQUES_ALLOW_SEND_TO_RECEIVER
        messageOnType: 'BOTH',        // CHEQUES_ALLOW_MESSAGE_ON_TYPE — NONE|PHYSICAL|DIGITAL|BOTH
    },
    // CHEQUES_TEXT_LOCATION — where the page text sits next to the voucher form
    // (top|left|right|bottom; on mobile the storefront always puts it on top).
    textLocation: 'top',
};

export const BRANDS_CONFIG_DEFAULT = {
    whatToShow:  { onlyOnline: false, navigationCategory: '' },
    layout: 'grouped',
    settings: {
        displayContinuous: 'logo',
        displayGrouped:    'text',
        showLettersWithoutBrands: false,
        letterColumns: 4,
        brandsPerLetter: 7,
    },
    textStyles: {
        desktop: {
            brandName:    { size: 14, color: '#111111', bold: false, italic: false, underline: false },
            alphabetNav:  { size: 12, color: '#111111', bold: false, italic: false, underline: false },
            letterHeading:{ size: 18, color: '#111111', bold: true,  italic: false, underline: false },
        },
        mobile: {
            brandName:    { size: 12, color: '#111111', bold: false, italic: false, underline: false },
            alphabetNav:  { size: 11, color: '#111111', bold: false, italic: false, underline: false },
            letterHeading:{ size: 16, color: '#111111', bold: true,  italic: false, underline: false },
        },
    },
};

export const OVERIGE_CONFIG_DEFAULT = {
    cookies: {
        enabled: true,      // own banner (cookie.js) on; off = cookie.js.off, e.g. a shop on Cookiebot
        categories: { necessary: true, analytics: true, marketing: true, personalization: false },
        bannerStyle: 'bottom',
        position:    'bottom',
    },
    trackingTools: [],
    toolToAdd: '',
    // mig215: who sends the Meta e-commerce events ('webshop' | 'gtm') and
    // ecommerce.items as a list (GA4 standard) on one-product events
    trackingEvents: { metaEvents: 'webshop', ga4ItemsArray: false },
    customCode: {
        expanded: true,
        section: 'head', // 'head' | 'body'
        head: '',
        body: '',
    },
};

export const STL_CONFIG_DEFAULT = {
    introText: { nl: '', en: '', fr: '' },
    introEditor: 'visual',  // 'visual' | 'html' — UI only
    looksGrid: {
        navigationCategory: '1',       // LOOKS_NAVIGATION_CODE — bare category index
        displayOrder: 'SORT_DEFAULT',  // LOOKS_LIST_SORTING
        looksPerRow: 3,                // MAX_COLUMNS_LOOKS
        showInIframe: false,           // pending backend
    },
    overlay: {
        onlyOnHover: false,
        bandBg: 'rgba(0, 0, 0, 0.1)',
        bandHeightPct: 100,
        bandVerticalPosition: 'middle',
        showTitle: true,
        titleColor: '#ffffff',
        titleFontSize: 12,
        showCta: true,
        ctaText: { nl: 'Bewonder', en: 'View look', fr: 'Voir le look' }, // i18n GLOBAL.LOOKS_DETAILS
        ctaBg: '#000000',
        ctaTextColor: '#ffffff',
        ctaFontSize: 12,
    },
    detailPage: {
        descriptionPosition: 'top',    // LOOKS_DETAIL_TEXT_LOCATION: 'top' | 'photo'
        showDividerBetweenProducts: false,
        showOutOfStock: false,
    },
    // chips ({ id, key } field / { id, text } literal), in display order —
    // SHOW_FIELDS_ON_LOOK_TITLE / _SUBHEADING templates (ProductTitleAndUrlCard helpers)
    productTitle:    [],
    productSubtitle: [],
};
